import { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  createInvestmentRequest,
  clearCreateInvestmentSuccess,
  getPlansRequest,
} from "../../redux/action";
import CurrencyDisplay from "../Currency/CurrencyDisplay.jsx";
import useCurrency from "../../hooks/useCurrency.js";
import { FaArrowUp } from "react-icons/fa";
import { PrimaryButton } from "../Buttons/index.js";

// Color mapping for different plans
const PLAN_COLORS = [
  "bg-primary-500",
  "bg-indigo-500",
  "bg-purple-500",
  "bg-emerald-500",
  "bg-orange-500",
  "bg-pink-500",
];

export const InvestForm = ({ handleClose }) => {
  const dispatch = useDispatch();
  const {
    getCurrencySymbol,
    convertUSDToDisplay,
    convertDisplayToUSD,
    isLoaded,
  } = useCurrency();
  const [amount, setAmount] = useState("");
  const [activeTier, setActiveTier] = useState(null);
  const [error, setError] = useState("");
  const [usdAmount, setUsdAmount] = useState(0);

  const {
    createInvestmentSuccess,
    createInvestmentError,
    createInvestmentLoading,
    user,
    plans,
    getPlansLoading,
  } = useSelector((state) => ({
    createInvestmentSuccess: state.ajaxStatuses.success.createInvestment,
    createInvestmentError: state.ajaxStatuses.errors.createInvestment,
    createInvestmentLoading: state.loadingIndicator.createInvestmentLoading,
    user: state.userData.user,
    plans: state.planData.plans || [],
    getPlansLoading: state.loadingIndicator.getPlansLoading,
  }));

  // Transform plans data to match the TIERS structure
  const TIERS = useMemo(() => {
    return plans.map((plan, index) => ({
      name: plan.planName,
      min: plan.minAmount,
      max: 100000,
      rate: plan.profitProduction,
      color: PLAN_COLORS[index % PLAN_COLORS.length],
      duration: plan.duration,
      _id: plan._id,
    }));
  }, [plans]);

  // --- FIX IS HERE ---
  // Only fetch plans if we don't have them yet.
  // This prevents the parent from unmounting the card grid (and this modal) unnecessarily.
  useEffect(() => {
    if (!plans || plans.length === 0) {
      dispatch(getPlansRequest());
    }
  }, [dispatch, plans]);

  // --- Logic ---

  // Calculate tier and projected returns whenever amount changes
  const calculations = useMemo(() => {
    if (!isLoaded || !amount || TIERS.length === 0) {
      return { tier: null, profit: 0, total: 0, error: "" };
    }

    const numAmount = Number(amount);
    const currencySymbol = getCurrencySymbol();

    // Convert user input from display currency to USD
    const usdInputAmount = convertDisplayToUSD(numAmount);
    setUsdAmount(usdInputAmount);

    // Convert USD tier limits to display currency for comparison
    const tierComparisons = TIERS.map((tier) => ({
      ...tier,
      minDisplay: convertUSDToDisplay(tier.min),
      maxDisplay: convertUSDToDisplay(tier.max),
    }));

    // Convert user balance from USD to display currency for comparison
    const balanceInDisplayCurrency = convertUSDToDisplay(user.balance);

    // Find active tier using display currency amounts
    const tier = tierComparisons.find(
      (t) => numAmount >= t.minDisplay && numAmount <= t.maxDisplay,
    );

    // Check minimum investment (check against smallest plan if no tier found)
    if (
      numAmount > 0 &&
      !tier &&
      tierComparisons.length > 0 &&
      numAmount < tierComparisons[0].minDisplay
    ) {
      return {
        error: `Minimum investment is ${currencySymbol}${tierComparisons[0].minDisplay.toFixed(
          2,
        )}`,
        profit: 0,
        total: 0,
        tier: null,
      };
    }

    // Check sufficient balance (compare in display currency)
    if (numAmount > balanceInDisplayCurrency) {
      return {
        error: "Insufficient balance",
        profit: 0,
        total: 0,
        tier: null,
      };
    }

    // Calculate profit and total using USD amounts (for accuracy)
    if (tier) {
      const profit = usdInputAmount * (tier.rate / 100);
      const total = usdInputAmount + profit;

      return {
        tier,
        profit,
        total,
        error: "",
      };
    }

    return { tier: null, profit: 0, total: 0, error: "" };
  }, [
    amount,
    user.balance,
    getCurrencySymbol,
    convertUSDToDisplay,
    convertDisplayToUSD,
    isLoaded,
    TIERS,
  ]);

  // Update local state based on calculations
  useEffect(() => {
    setActiveTier(calculations.tier);
    setError(calculations.error);
  }, [calculations]);

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {
      if (!amount || error || !activeTier) return;

      const payload = {
        user: user._id,
        amount: usdAmount, // Use USD amount for API
        expectedReturn: calculations.total,
        profitProduction: activeTier.rate,
        planName: activeTier.name,
      };

      dispatch(createInvestmentRequest(payload));
    },
  });

  // Handle Redux Side Effects
  useEffect(() => {
    if (createInvestmentError) toast.error(createInvestmentError);
  }, [createInvestmentError]);

  useEffect(() => {
    if (createInvestmentSuccess) {
      if (handleClose) handleClose();
      dispatch(clearCreateInvestmentSuccess());
      setAmount("");
    }
  }, [createInvestmentSuccess, dispatch, handleClose]);

  const handleMax = () => {
    const maxDisplayAmount = convertUSDToDisplay(user.balance);
    setAmount(maxDisplayAmount.toString());
  };

  return (
    <div className="w-[428px] mx-auto bg-[#f8faff] min-h-screen relative font-sans text-slate-900 pb-24 shadow-[0_0_50px_rgba(0,0,0,0.1)] overflow-y-auto overflow-x-hidden">
      {/* --- Background Decorative Elements --- */}
      <div
        className="absolute top-0 right-0 w-full h-[350px] bg-no-repeat bg-right-top opacity-50 mix-blend-multiply pointer-events-none"
        style={{
          backgroundImage: "url('/img/new/background/swoosh-bg.png')",
          backgroundSize: "cover",
        }}
      ></div>

      {/* --- Top Bar --- */}
      <div className="relative z-20 px-5 pt-12 pb-6">
        <button
          onClick={handleClose}
          className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-full border border-gray-100 shadow-[0_2px_10px_rgb(0,0,0,0.02)] text-[#1a4bdf] font-medium text-[13px] active:scale-95 transition-transform"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Plans
        </button>
      </div>

      {/* --- Balance Header Section --- */}
      <div className="relative z-10 px-6 text-center mt-2">
        <p className="text-[13px] text-slate-500 font-medium mb-1">
          Available to Invest
        </p>

        {/* Balance Amount */}
        <h1 className="text-[34px] font-bold text-[#071330] tracking-tight leading-none mb-3">
          {/* Use CurrencyDisplay here, passing the balance */}
          <CurrencyDisplay usdAmount={user.balance} className="inline" />
        </h1>

        {/* Small Blue Divider Line */}
        <div className="w-8 h-[3px] bg-[#1a4bdf] mx-auto rounded-full mb-4"></div>

        {/* Security Badge */}
        <div className="inline-flex items-center gap-2 bg-[#f0f4ff] border border-[#dce5fc] px-4 py-1.5 rounded-full">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1a4bdf"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            <polyline points="9 12 11 14 15 10"></polyline>
          </svg>
          <span className="text-[11px] text-slate-500 font-medium">
            Your funds are secure and ready to invest
          </span>
        </div>

        {/* 3D Graphics Placeholder (Chart and Coins) */}
        <div className="absolute top-[-30px] -right-4 w-[140px] pointer-events-none -z-10">
          <img
            src="/img/new/background/coins-chart.jpeg"
            alt="Investment Growth"
            className="w-full h-auto object-contain"
          />
        </div>
      </div>

      {/* --- Main Investment Card --- */}
      <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-gray-50 mx-4 mt-10 relative z-20">
        {/* Top Wallet Icon */}
        <div className="w-12 h-12 bg-[#f0f4ff] rounded-full mx-auto flex items-center justify-center text-[#1a4bdf] mb-4">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path>
            <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path>
            <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path>
          </svg>
        </div>

        <h2 className="text-center text-[17px] font-bold text-[#071330] mb-1">
          Enter Amount
        </h2>
        <p className="text-center text-[12px] text-slate-500 font-medium mb-6">
          Choose the amount you want to invest
        </p>

        {/* Amount Input Group */}
        <div className="flex border-2 border-[#1a4bdf] rounded-xl overflow-hidden mb-6 transition-all bg-white shadow-[0_4px_15px_rgba(26,75,223,0.08)]">
          {/* Currency Prefix */}
          <div className="w-14 bg-[#f4f7ff] flex items-center justify-center text-[#1a4bdf] font-bold text-[18px] border-r border-[#e5edff]">
            {getCurrencySymbol ? getCurrencySymbol() : "K"}
          </div>

          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="flex-1 py-4 px-4 text-[17px] font-bold text-slate-800 placeholder-slate-300 focus:outline-none bg-transparent"
          />

          <button
            type="button"
            onClick={handleMax}
            className="px-4 text-[13px] font-bold text-[#1a4bdf] hover:bg-blue-50 transition-colors"
          >
            MAX
          </button>
        </div>

        {/* Percentage Selectors */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <button
            type="button"
            onClick={() => {
              const displayAmount = convertUSDToDisplay(user.balance * 0.25);
              setAmount(displayAmount.toFixed(2));
            }}
            className="border border-gray-100 rounded-xl py-3 flex items-center justify-center gap-1.5 hover:bg-blue-50 hover:border-blue-100 transition-colors"
          >
            {/* 25% Pie Icon */}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1a4bdf"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path>
              <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
            </svg>
            <span className="text-[13px] font-bold text-[#1a4bdf]">25%</span>
          </button>

          <button
            type="button"
            onClick={() => {
              const displayAmount = convertUSDToDisplay(user.balance * 0.5);
              setAmount(displayAmount.toFixed(2));
            }}
            className="border border-gray-100 rounded-xl py-3 flex items-center justify-center gap-1.5 hover:bg-blue-50 hover:border-blue-100 transition-colors"
          >
            {/* 50% Pie Icon */}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1a4bdf"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path>
              <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
            </svg>
            <span className="text-[13px] font-bold text-[#1a4bdf]">50%</span>
          </button>

          <button
            type="button"
            onClick={handleMax}
            className="border border-gray-100 rounded-xl py-3 flex items-center justify-center gap-1.5 hover:bg-blue-50 hover:border-blue-100 transition-colors"
          >
            {/* 100% Circle Icon */}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1a4bdf"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
            </svg>
            <span className="text-[13px] font-bold text-[#1a4bdf]">100%</span>
          </button>
        </div>

        {/* Error State (Hidden if no error) */}
        {error && (
          <div className="bg-red-50 text-red-600 text-xs font-medium py-2.5 px-4 rounded-xl mb-4 border border-red-100 text-center">
            {error}
          </div>
        )}

        {/* Information Banner */}
        <div className="bg-[#f4f7ff] border border-[#dce5fc] rounded-xl p-3.5 flex items-start gap-3 mb-6">
          <div className="w-4 h-4 rounded-full bg-[#1a4bdf] flex items-center justify-center text-white shrink-0 mt-0.5">
            <span className="text-[10px] font-bold font-serif italic">i</span>
          </div>
          <span className="text-[11px] text-slate-600 font-medium leading-relaxed">
            Minimum investment amount may apply depending on the plan.
          </span>
        </div>

        {/* Submit Button */}
        <button
          type="button"
          onClick={formik?.handleSubmit}
          disabled={
            !amount ||
            !!error ||
            !activeTier ||
            !isLoaded ||
            getPlansLoading ||
            createInvestmentLoading
          }
          className="w-full bg-[#1a4bdf] hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl py-4 flex items-center justify-center relative shadow-[0_8px_20px_rgba(26,75,223,0.25)] transition-all active:scale-[0.98]"
        >
          <div className="flex items-center gap-2">
            {/* Lock Icon */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span className="text-[15px] font-semibold tracking-wide">
              {createInvestmentLoading ? "Processing..." : "Confirm Investment"}
            </span>
          </div>
          {/* Right Arrow */}
          <div className="absolute right-4">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
};
